import { useArrowSource } from '@components/ArtBoard/Arrows/hooks/useArrowSource'
import type { ArrowUI } from '@components/ArtBoard/Arrows/types'
import SmartArrow from '@lib/components/SmartArrow'
import { useClickOutside } from '@lib/hooks'
import type { ArrowStyleType } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import React, { useLayoutEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { NODE_GHOST } from '@/constants/constants'
import { isSafari } from '@/helpers'
import { useScaleZoom } from '@/hooks'
import { clsJoin, isInsidePopover } from '@/utils'
import { ARROW_POINTER_LIMIT } from '../../constants'
import {
  isInsideArrowLabel,
  isInsideArrowToolbar,
} from '../ArrowActionsToolbar'
import { ArrowLineAnchor } from './components/ArrowLineAnchor'
import css from './styles.module.css'

export const ArrowLine: React.FC<ArrowUI> = ({
  colorId,
  id,
  visible,
  relation,
  points,
  model,
  opacityState,
  isSelected,
  isHighlighted,
}) => {
  const dispatch = useDispatch()

  const { color, nodeVariant: nodeStartVariant } = useArrowSource(
    relation[0],
    colorId
  )

  const [isArrowVisible, setIsArrowVisible] = useState(visible)

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const { scale } = useScaleZoom()

  const zIndex = isSelected ? 999 : 0
  const options = {
    strokeWidth: 2 * scale,
    headSize: 5,
    headColor: color,
    lineColor: color,
    dashness: !isSafari && !isSelected && !isHighlighted,
    curveness: 0,
    path: 'straight' as ArrowStyleType,
  }

  const onClickArrow = () => {
    if (isReadOnly) {
      return
    }

    dispatch({
      type: CanvasActionTypes.SET_SELECTED_ARROW_ID,
      payload: {
        id,
      },
    })
  }

  const onClickOutside = ({ target }: React.MouseEvent) => {
    if (
      points.length > 0 ||
      isInsideArrowToolbar(target) ||
      isInsideArrowLabel(id, target) ||
      isInsidePopover(target)
    ) {
      return
    }

    dispatch({
      type: CanvasActionTypes.RESET_SELECTED_ARROW_ID,
    })
  }

  const clickOutside = useClickOutside<HTMLDivElement>(
    isSelected as boolean,
    onClickOutside,
    onClickArrow
  )

  const [rerenderId, setRerenderId] = useState(Math.random)

  const style = React.useMemo(
    () => ({
      '--arrow-color': color,
      zIndex,
    }),
    [color, zIndex]
  )

  const { variant: nodeEndVariant } = useSelector(
    migrationProjectSelector.getProjectNodeById(relation[1])
  )

  useLayoutEffect(() => {
    if (!isSelected) {
      const startElement = document.getElementById(relation[0])
      const endElement = document.getElementById(relation[1])

      if (!startElement || !endElement) {
        setIsArrowVisible(false)
      } else {
        setTimeout(() => {
          setRerenderId(Math.random)
        }, 100)
      }
    }
  }, [])

  if (
    !isArrowVisible ||
    nodeEndVariant === NODE_GHOST ||
    nodeStartVariant === NODE_GHOST
  ) {
    return null
  }

  return (
    <div
      className={clsJoin(css.container, 'Arrow-line', `Arrow-model-${model}`)}
      data-arrow-id={id}
      style={style}
      {...clickOutside}
    >
      <div
        className={clsJoin(css.arrow, css[opacityState])}
        data-id={rerenderId}
      >
        <div /* Без этого div ломается png экспорт */>
          <SmartArrow
            {...options}
            start={relation[0]}
            end={points.length ? points[0].id : relation[1]}
            showHead={!points.length}
            labels={
              <div>
                {!points.length && isSelected && (
                  <div
                    data-arrow-toolbar-anchor={id}
                    style={{ position: 'absolute' }}
                  />
                )}
                {points.length < ARROW_POINTER_LIMIT && (
                  <div className={css.addAnchor}>
                    <ArrowLineAnchor id={id} index={0} />
                  </div>
                )}
              </div>
            }
          />
        </div>

        {points.map((point: any, index: number) => {
          const isLast = index === points.length - 1
          const endId = isLast ? relation[1] : points[index + 1].id

          return (
            <div key={point.id} /* Без этого div ломается png экспорт */>
              <SmartArrow
                {...options}
                start={points[index].id}
                end={endId}
                showHead={isLast}
                labels={
                  <div>
                    {isLast && isSelected && (
                      <div
                        data-arrow-toolbar-anchor={id}
                        style={{ position: 'absolute' }}
                      />
                    )}
                    {points.length < ARROW_POINTER_LIMIT && (
                      <div className={css.addAnchor}>
                        <ArrowLineAnchor id={id} index={index + 1} />
                      </div>
                    )}
                  </div>
                }
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
